// © 2015-2021 WDNA - All Rights Reserved

import * as React from 'react';
import _ from 'lodash';

import { WidgetGrid, JarvisWidget } from 'wdna-components/lib';
import { removeTab } from 'wdna-components/lib/tabs';
import { initProcess, finishProcess } from 'wdna-components/lib/scheduler/actions';
import Loader from 'wdna-components/lib/loaders/local/Loader';
import UiValidate from 'wdna-components/lib/forms/validation/UiValidate';
import { messageError, messageInfo } from 'wdna-components/lib/helpers/notification';
import { getErrorMessage } from 'wdna-components/lib/helpers/response';
import { hasLevel } from 'wdna-components/lib/helpers/permission-new';

import { typeSensorVariable, TypeSensorVariableDO, variable, VariableDO } from '../../../requests';

import ButtonsForm from '../../../../../components/ButtonsForm';
import Form from './Form';

interface Props {
  rowId?: number;
  notify?: () => void;
  typeSensorId: number;
}

interface State {
  loading: boolean;
  variableArray: VariableDO[];
  data: Partial<TypeSensorVariableDO>;
}

export default class Edit extends React.Component<Props, State> {
  private pid: string;
  private mounted: boolean;
  private defaultValues: any;

  public constructor(props: Props) {
    super(props);

    this.pid = window.getStr();
    this.mounted = false;
    this.defaultValues = {
      typeSensorId: this.props.typeSensorId,
    };

    this.state = {
      loading: false,
      variableArray: [],

      data: {
        typeSensorId: this.props.typeSensorId,
      }
    };
  }

  public componentDidMount(): void {
    this.mounted = true;
    this.load().catch(() => { /* pass */ });
  }

  public componentWillUnmount(): void {
    this.mounted = false;
  }

  public setLocalState = (nextState: Partial<State>, callback?: (() => void)): void => {
    if (this.mounted) {
      this.setState((prevState: State) => ({
        ...prevState,
        ...nextState,
      }), callback);
    }
  };

  public load = async (): Promise<void> => {
    const { rowId } = this.props;

    this.loadingStart();
    try {
      const variableArray: VariableDO[] = await variable<VariableDO[]>('list') || [];

      let doTypeSensorVariable: Partial<TypeSensorVariableDO> | undefined = this.state.data;
      if (_.isFinite(rowId)) doTypeSensorVariable = await typeSensorVariable<TypeSensorVariableDO>('get', String(rowId));

      this.setLocalState(
        {
          variableArray,
          data: doTypeSensorVariable,
        },
        () => {
          this.defaultValues = _.cloneDeep(doTypeSensorVariable);
        }
      );
    } catch (error) {
      messageError('Error', getErrorMessage(error));
    }
    this.loadingFinish();
  };

  public loadingStart = (): void => {
    this.setLocalState({ loading: true });
    window.store.dispatch(initProcess(this.pid));
  };

  public loadingFinish = (): void => {
    this.setLocalState({ loading: false });
    window.store.dispatch(finishProcess(this.pid));
  };

  public onReset = (): void => {
    this.setLocalState({ data: { ...this.defaultValues } });
  };

  public submit = (e: React.FormEvent<HTMLFormElement>): void => {
    e.preventDefault();
    const { data } = this.state;
    if (!this.isValidForm(data)) return;

    // Fix data types
    data.order = _.toNumber(data.order);
    data.bytes = _.toNumber(data.bytes);
    data.variableId = _.toNumber(data.variableId);
    data.typeSensorId = _.toNumber(data.typeSensorId);
    // Optional
    data.discardMin = (_.isEmpty(data.discardMin) && data.discardMin !== 0) ? null : _.toNumber(data.discardMin);
    data.discardMax = (_.isEmpty(data.discardMax) && data.discardMax !== 0) ? null : _.toNumber(data.discardMax);
    data.adjustMin = (_.isEmpty(data.adjustMin) && data.adjustMin !== 0) ? null : _.toNumber(data.adjustMin);
    data.adjustMax = (_.isEmpty(data.adjustMax) && data.adjustMax !== 0) ? null : _.toNumber(data.adjustMax);

    this.loadingStart();
    typeSensorVariable<TypeSensorVariableDO>('set', undefined, data)
      ?.then(() => {
        messageInfo('Success', `Sensor variable successfully ${data.id ? 'edited' : 'added'}`, 5000);
        const { notify } = this.props;
        if (typeof notify === 'function') {
          notify();
        }
        window.store.dispatch(removeTab('STVE'));
      })
      .catch((error: any) => {
        messageError('Error', getErrorMessage(error));
      })
      .finally(() => {
        this.loadingFinish();
      });
  };

  public isValidForm = (data: Partial<TypeSensorVariableDO>): boolean => {
    if (!data.variableId) {
      messageError('Error', 'Please select a variable');
      return false;
    }
    return true;
  };

  public handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>): void => {
    const { name, value, checked, type, dataset } = (e as React.ChangeEvent<HTMLInputElement>).target;
    const { ctype } = dataset || {};
    // Process new value from ctype
    let newValue: any = (type === 'checkbox') ? !!checked : value;
    if (ctype === 'number') newValue = (/^\d+(\.\d+){0,1}$/.test(value)) ? _.toNumber(value) : null;
    // Create new data state structure
    const levels: string[] = name.split('.');
    const nextState: Record<string, any> = { [levels[0]]: { ...this.state[levels[0]] } };
    _.set(nextState, levels, newValue);
    this.setLocalState(nextState);
  };

  public render(): JSX.Element {
    const { loading, data } = this.state;

    return (
      <div id="content" className="content">
        <WidgetGrid>
          <div className="row">
            <article className="col-sm-12 col-md-12 col-lg-12">
              <JarvisWidget
                editbutton={false}
                togglebutton={false}
                colorbutton={false}
                deletebutton={false}
                custombutton={false}
                fullscreenbutton={false}
              >
                <div className="widget-body">
                  <Loader show={loading} overlay={false} />
                  <UiValidate>
                    <form className="smart-form" onSubmit={this.submit} method="post" encType="multipart/form-data">
                      <header>
                        <h2>
                          <span className="widget-icon" style={{ padding: '8px' }}>
                            <i className="fa fa-edit" />
                          </span>
                          Variable (Sensor-Type) {data.id ? `Edit: ${data.id}` : 'New'}
                        </h2>
                      </header>
                      <Form formValues={this.state} onChange={this.handleChange} />
                      <ButtonsForm loading={loading} disabled={!hasLevel('role', ['ADM'])} onReset={this.onReset} />
                    </form>
                  </UiValidate>
                </div>
              </JarvisWidget>
            </article>
          </div>
        </WidgetGrid>
      </div>
    );
  }
}
